import { useReactiveVar } from '@apollo/client';
import { noop } from 'lodash';
import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';

import { isLoggedInVar } from '@store/reactiveVars';

type Props = {
  children: ReactElement
};

/**
 * Bridges react-oidc-context auth events into app state:
 *   - Keeps isLoggedInVar in sync with auth.isAuthenticated.
 *   - On access-token expiry or silent-renew failure, stashes the current
 *     returnUrl, signs out, and redirects back through the IdP.
 *
 * Fixes over the previous implementation:
 *   - Listeners are kept in refs so cleanup actually removes them. The old
 *     code passed fresh `() => noop()` arrows to the `remove*` methods; those
 *     were different function references from the registered handlers, so
 *     nothing was ever unsubscribed. On auth-provider re-init we'd leak
 *     duplicate handlers that each trigger their own signinRedirect.
 *   - Error logs are gated on NODE_ENV. The raw `error` from oidc-client-ts
 *     includes the user's `sub` and (sometimes) partial token contents.
 */
function SessionManager({ children }: Props) {
  const auth = useAuth();
  const location = useLocation();
  const hasShownExpiredMessage = useRef(false);
  const loggedInVar = useReactiveVar(isLoggedInVar);

  const handleSessionExpired = useCallback(async () => {
    if (hasShownExpiredMessage.current) return;
    hasShownExpiredMessage.current = true;

    const returnUrl = location.pathname + location.search;
    try { sessionStorage.setItem('returnUrl', returnUrl); } catch { /* private mode */ }

    await auth.removeUser();
    await auth.signinRedirect();
  }, [auth, location.pathname, location.search]);

  useEffect(() => {
    const { events } = auth;

    const onExpiring = noop;
    const onExpired = () => {
      handleSessionExpired().catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[SessionManager] expired-handler failed:', err);
        }
      });
    };
    const onSilentRenewError = (_error: unknown) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[SessionManager] silent renew error');
      }
      handleSessionExpired().catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[SessionManager] silent-renew fallback failed:', err);
        }
      });
    };

    events.addAccessTokenExpiring(onExpiring);
    events.addAccessTokenExpired(onExpired);
    events.addSilentRenewError(onSilentRenewError);

    return () => {
      events.removeAccessTokenExpiring(onExpiring);
      events.removeAccessTokenExpired(onExpired);
      events.removeSilentRenewError(onSilentRenewError);
    };
  }, [auth, handleSessionExpired]);

  useEffect(() => {
    if (loggedInVar !== auth.isAuthenticated) {
      isLoggedInVar(auth.isAuthenticated);
    }
  }, [auth.isAuthenticated, loggedInVar]);

  return children;
}

export default SessionManager;
